/**
 * Permission Utilities
 *
 * Helper functions for sanitizing and validating permission/module data.
 * Handles legacy data corruption where modules may contain permission-format strings.
 */

import type { ModulePermissions } from "@/services/api/user/types";

/**
 * Sanitizes a module name by stripping any permission suffixes.
 *
 * Some legacy data has modules stored as "module:*" instead of just "module".
 * This function normalizes module names by removing any ":*" or ":read/:write" suffixes.
 *
 * @param module - The module string to sanitize
 * @returns The sanitized module name (base module without permission suffixes)
 *
 * @example
 * sanitizeModule("report_builder:*") // returns "report_builder"
 * sanitizeModule("dashboard:read")   // returns "dashboard"
 * sanitizeModule("portfolio")        // returns "portfolio" (unchanged)
 */
export function sanitizeModule(module: string): string {
  if (!module) return module;

  // Strip any :* or :action suffix (e.g., "module:*", "module:read", "module:write")
  const colonIndex = module.indexOf(':');
  if (colonIndex > 0) {
    return module.substring(0, colonIndex);
  }

  return module;
}

/**
 * Sanitizes an array of module names.
 *
 * @param modules - Array of module strings to sanitize
 * @returns Array of sanitized, unique module names
 *
 * @example
 * sanitizeModules(["report_builder:*", "dashboard", "portfolio:*"])
 * // returns ["report_builder", "dashboard", "portfolio"]
 */
export function sanitizeModules(modules: string[]): string[] {
  if (!modules || !Array.isArray(modules)) return [];

  // Sanitize each module and remove duplicates
  const sanitized = modules.map(sanitizeModule);
  return [...new Set(sanitized)];
}

/**
 * Generates permission strings from module names.
 *
 * Creates the standard "module:*" permission format for each module.
 *
 * @param modules - Array of module names (will be sanitized first)
 * @returns Array of permission strings in "module:*" format
 *
 * @example
 * generatePermissionsFromModules(["dashboard", "portfolio"])
 * // returns ["dashboard:*", "portfolio:*"]
 */
export function generatePermissionsFromModules(modules: string[]): string[] {
  const sanitizedModules = sanitizeModules(modules);
  return sanitizedModules.map((m) => `${m}:*`);
}

/**
 * Checks if a modules array contains corrupted data (permission-format strings).
 *
 * @param modules - Array of module strings to check
 * @returns true if any module contains a colon (indicating corruption)
 *
 * @example
 * hasCorruptedModules(["report_builder:*", "dashboard"]) // returns true
 * hasCorruptedModules(["report_builder", "dashboard"])   // returns false
 */
export function hasCorruptedModules(modules: string[]): boolean {
  if (!modules || !Array.isArray(modules)) return false;
  return modules.some((m) => m.includes(':'));
}

/**
 * Validates and sanitizes user permission data for API submission.
 *
 * Ensures modules are in correct format and permissions are properly generated.
 * This is a defensive function to prevent sending malformed data to the API.
 *
 * @param modules - Array of selected module names
 * @param permissions - Array of selected permission strings
 * @returns Object with sanitized modules and permissions
 */
function normalizeModuleId(module: string): string {
  return module.toLowerCase().replace(/-/g, "_");
}

function moduleToHyphenated(module: string): string {
  return module.toLowerCase().replace(/_/g, "-");
}

/**
 * True when `permissions` includes an explicit `module:*` grant for the module
 * (underscore or hyphen form, e.g. `pulse_v2:*` or `pulse-v2:*`).
 */
export function hasExplicitModulePermissionGrant(
  module: string,
  permissions: string[],
): boolean {
  const normalizedModule = normalizeModuleId(module);
  const hyphenatedModule = moduleToHyphenated(module);

  return (
    permissions.includes(`${normalizedModule}:*`) ||
    permissions.includes(`${hyphenatedModule}:*`)
  );
}

/**
 * Returns whether the user may access a module.
 *
 * Matches wildcard `*`, explicit `module:*` permission grants, `modules` entries,
 * and legacy module names stored with action suffixes in `modules`.
 */
export function hasModulePermission(
  module: string,
  data: Pick<ModulePermissions, "modules" | "permissions"> | null | undefined,
): boolean {
  if (!data) return false;

  const normalizedModule = normalizeModuleId(module);

  // Settings is available to every authenticated user (personal preferences).
  if (normalizedModule === "settings") {
    return true;
  }

  if (
    data.permissions.includes("*") ||
    data.permissions.includes("admin:*")
  ) {
    return true;
  }

  if (hasExplicitModulePermissionGrant(module, data.permissions)) {
    return true;
  }

  if (data.modules.includes("*")) {
    return true;
  }

  if (data.modules.includes(normalizedModule)) {
    return true;
  }

  return sanitizeModules(data.modules).includes(normalizedModule);
}

/**
 * Strict membership check: ignores `*` / `admin:*` wildcards.
 *
 * Used by UI surfaces that should reflect the user's *configured* modules
 * rather than effective access — e.g. the aniOS dock and Apps popover, where
 * admins should still see only the apps explicitly assigned to them.
 *
 * For actual access control (route guards, API gates), keep using
 * {@link hasModulePermission}, which honours wildcards.
 */
export function hasModuleMembership(
  module: string,
  data: Pick<ModulePermissions, "modules" | "permissions"> | null | undefined,
): boolean {
  if (!data) return false;

  const normalizedModule = normalizeModuleId(module);

  if (hasExplicitModulePermissionGrant(module, data.permissions ?? [])) {
    return true;
  }

  if (data.modules.includes(normalizedModule)) {
    return true;
  }

  return sanitizeModules(data.modules).includes(normalizedModule);
}

export function hasNamedPermission(
  permissions: string[] | undefined,
  named: string,
): boolean {
  if (!permissions?.length) return false;
  if (permissions.includes("*") || permissions.includes("admin:*")) {
    return true;
  }
  if (permissions.includes(named)) {
    return true;
  }
  const [module] = named.split(":");
  return Boolean(module && permissions.includes(`${module}:*`));
}

export function preparePermissionData(
  modules: string[],
  permissions: string[]
): { modules: string[]; permissions: string[] } {
  // If user has superadmin ("*") permission, preserve it
  if (permissions.includes('*')) {
    return {
      modules: sanitizeModules(modules),
      permissions: ['*'],
    };
  }

  // Sanitize modules first
  const sanitizedModules = sanitizeModules(modules);

  // Generate correct permissions from sanitized modules
  const generatedPermissions = generatePermissionsFromModules(sanitizedModules);

  return {
    modules: sanitizedModules,
    permissions: generatedPermissions,
  };
}
